import { getUniqueId, parseNumberStrict, removeNulls, safeParseNullDefault } from 'shouldve-been-js';
import AppConfig from '../Models/AppConfigModel';
import BugReport from '../Models/BugReportModel';
import CurrentUser from '../Models/CurrentUserModel';
import KFleetToken from '../Models/KFleetTokenModel';
import SystemInfo from '../Models/SystemInfoModel';
import Tenant from '../Models/TenantModel';
import TenantToken from '../Models/TenantTokenModel';
import StatusDate from '../Models/StatusDateModel';
import Order from '../Models/OrderModel';
import OrderValidationResult from '../Models/OrderValidationResultModel';
import { datetimeToApiString } from '../Utils/DateTimeParser';
import Vessel from '../Models/VesselModel';

class ApiService {

    private _baseUrl: string;
    private _addBugReport: (newBugReport: BugReport) => void;

    public constructor(baseUrl: string, addBugReport: (newBugReport: BugReport) => void) {
        this._baseUrl = baseUrl;
        this._addBugReport = addBugReport;
    }

    public get baseUrl(): string {
        return this._baseUrl;
    }

    public set baseUrl(value: string) {
        this._baseUrl = value;
    }

    public get addBugReport(): (newBugReport: BugReport) => void {
        return this._addBugReport;
    }

    public set addBugReport(value: (newBugReport: BugReport) => void) {
        this._addBugReport = value;
    }

    public copy(): ApiService {
        const newApi: ApiService = new ApiService(this.baseUrl, this.addBugReport);
        return newApi;
    }

    public async getHealth(token: string | null): Promise<boolean> {

        const endpointUrl: string = '/int-v1/system/health';

        try {
            const response: Response = token === null //endpoint only needs a token if used through the tunnel
                ? await fetch(
                    this.baseUrl + endpointUrl, 
                    { 
                        mode: 'cors',
                        method: 'GET',
                    },
                )
                : await fetch(
                    this.baseUrl + endpointUrl, 
                    { 
                        mode: 'cors',
                        method: 'GET',
                        headers: {
                            Authorization: 'Bearer ' + token,
                        },
                    },
                );

            if (response.status >= 400) {

                return false;
            }

            return true;       
        } catch (e: unknown) {
            return false;  
        }
    }

    public async getSystemInfo(token: string | null): Promise<SystemInfo | null> {

        if (token === null) { //endpoint strictly only requires a token if used through a tunnel, but as a token should always be available when getting this, the check is here
            return null;
        }

        const endpointUrl: string = '/int-v1/system';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'System Info', response.status.toString() + ' ' + (await response.text())));
                return null;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            return SystemInfo.fromJson(jsonResponse);
            
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'System Info', e?.message ?? ''));
            return null;  
        }
    }

    public async getCompanyLogo(token: string | null): Promise<Blob | null> {

        if (token === null) { //endpoint strictly only requires a token if used through a tunnel, but as a token should always be available when getting this, the check is here
            return null;
        }

        const endpointUrl: string = '/int-v1/system/logo?module=inventory&format=png&transparentPng=true';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Company Logo', response.status.toString() + ' ' + (await response.text())));
                return null;
            }

            return await response.blob();

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Company Logo', e?.message ?? ''));
            return null;  
        }
    }

    public async getAppConfig(token: string | null): Promise<AppConfig | null> {

        if (token === null) { //endpoint strictly only requires a token if used through a tunnel, but as a token should always be available when getting this, the check is here
            return null;
        }

        const endpointUrl: string = '/int-v1/system/appconfig/mobile/purchase';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'App Config', response.status.toString() + ' ' + (await response.text())));
                return null;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            return AppConfig.fromJson(jsonResponse);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'App Config', e?.message ?? ''));
            return null;  
        }
    }

    public async fetchNewKFleetTokenByUsernameAndPassword(username: string, password: string): Promise<KFleetToken | null> {

        const tokenUrl: string = '/int-v1/token';

        try {
            const response: Response = await fetch(
                this.baseUrl + tokenUrl, 
                { 
                    mode: 'cors',
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        username: username,
                        password: password,
                    }),
                },
            );

            if (response.status >= 400) { //if failed to fetch token
                return null;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            const tokenInfo: KFleetToken = KFleetToken.fromJson(jsonResponse);

            return tokenInfo;

        } catch (e: unknown) {
            return null;
        }
    }

    public static async fetchNewTenantTokenByEmailAndPassword(email: string, password: string, tenantDisplayName: string): Promise<TenantToken | null> {

        try {
            const response: Response = await fetch(
                'https://api.prod.k-fleet.cloud/credentials/login',
                { 
                    mode: 'cors',
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        email: email,
                        password: password,
                        tenantDisplayName: tenantDisplayName,
                    }),
                },
            );

            if (response.status >= 400) { //if failed to fetch token
                return null;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            const tokenInfo: TenantToken = TenantToken.fromJson(jsonResponse);

            return tokenInfo;

        } catch (e: unknown) {
            return null;
        }
    }

    public async fetchNewKFleetTokenByRefreshToken(refreshToken: string): Promise<KFleetToken | null | undefined> { //null means refresh token has expired, while undefined could simply mean you are offline

        const tokenUrl: string = '/int-v1/token';

        try {
            const response: Response = await fetch(
                this.baseUrl + tokenUrl, 
                { 
                    mode: 'cors',
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        refreshtoken: refreshToken,
                    }),
                },
            );

            if (response.status === 401) { //refresh token is already used or expired
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Token', 'Refresh token is already used or expired (K-Fleet)'));
                return null;
            }

            if (response.status >= 400) { //if failed to fetch token
                return undefined;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();
            
            const tokenInfo: KFleetToken = KFleetToken.fromJson(jsonResponse);

            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Token', 'New token has been fetched from refresh token (K-Fleet)'));

            return tokenInfo;

        } catch (e: unknown) {
            return undefined;
        }
    }

    public static async fetchNewTenantTokenByRefreshToken(refreshToken: string, addBugReport: (newBugReport: BugReport) => void): Promise<TenantToken | null | undefined> { //null means refresh token has expired, while undefined could simply mean you are offline

        try {
            const response: Response = await fetch(
                'https://api.prod.k-fleet.cloud/credentials/refreshtoken',
                { 
                    mode: 'cors',
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        refresh_token: refreshToken,
                    }),
                },
            );

            if (response.status >= 400) { //if failed to fetch token

                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                const textResponse: string = await response.text();

                if (textResponse === 'INVALID_REFRESH_TOKEN\n') { //refresh token is already used or expired
                    addBugReport(new BugReport(getUniqueId(), new Date(), 'Token', 'Refresh token is already used or expired (tenant)'));
                    return null;
                }

                return undefined;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();
            
            const tokenInfo: TenantToken = TenantToken.fromJson(jsonResponse);

            addBugReport(new BugReport(getUniqueId(), new Date(), 'Token', 'New token has been fetched from refresh token (tenant)'));

            return tokenInfo;

        } catch (e: unknown) {
            return undefined;
        }
    }

    public static async getTenantsByEmail(email: string): Promise<Tenant[]> {

        try {
            const response: Response = await fetch(
                'https://api.prod.k-fleet.cloud/tenants/user-tenants/' + email,
                { 
                    mode: 'cors',
                    method: 'GET',
                },
            );

            if (response.status >= 400) { //if failed to fetch token
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            if (!Array.isArray(jsonResponse)) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
            const tenants: (Tenant | null)[] = jsonResponse?.map((jsonObject: any) => {
                return Tenant.fromJson(jsonObject);
            });

            return removeNulls(tenants);  

        } catch (e: unknown) {
            return [];
        }
    }

    public async logout(token: string | null): Promise<boolean> {

        if (token === null) {
            return false;
        }

        const endpointUrl: string = '/int-v1/token';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'DELETE',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            ); 
            
            if (response.status >= 400) {
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Logout', response.status.toString() + ' ' + (await response.text())));
                return false;
            }

            return true;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Logout', e?.message ?? ''));
            return false;
        }
    }

    public async getUserAccess(userId: string, token: string | null): Promise<string[]> {

        if (token === null) {
            return [];
        }

        const endpointUrl: string = '/int-v1/users/' + userId + '/permissions';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'User Access', response.status.toString() + ' ' + (await response.text())));
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            if (!Array.isArray(jsonResponse.items)) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call
            const permissions: (string | null)[] = jsonResponse.items.map((item: any) => safeParseNullDefault<string>(item?.accessId, ''));

            return removeNulls(permissions);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'User Access', e?.message ?? ''));
            return [];
        }
    }

    public async getCurrentUserByEmail(email: string, token: string | null): Promise<CurrentUser | null> {

        if (token === null) {
            return null;
        }

        const endpointUrl: string = '/int-v1/users?loadPermissions=true&$top=1&$filter=email eq \'' + email + '\'';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'CurrentUser by Email', response.status.toString() + ' ' + (await response.text())));
                return null;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            if (!Array.isArray(jsonResponse.items)) {
                return null;
            }

            if (jsonResponse.items.length === 0) {
                return null;
            }

            return CurrentUser.fromJson(jsonResponse.items[0]);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'CurrentUser by Email', e?.message ?? ''));
            return null;
        }
    }

    public async getServerTimezoneOffsetMinutes(token: string | null): Promise<number | null> {

        if (token === null) { //endpoint strictly only requires a token if used through a tunnel, but as a token should always be available when getting this, the check is here
            return null;
        }

        const endpointUrl: string = '/int-v1/system/time';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'ServerTimezoneOffset', response.status.toString() + ' ' + (await response.text())));
                return null;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            const localTime: string | null = safeParseNullDefault<string>(jsonResponse?.localTime, '');

            if (localTime === null) {
                return null;
            }

            if (localTime.includes('+')) {

                const timezoneString: string = localTime.split('+')[1] ?? '';

                if (timezoneString.length !== 5 || timezoneString[2] !== ':') {
                    return null;
                }

                const hourPart: number | null = parseNumberStrict(timezoneString.substring(0, 2));
                const minutePart: number | null = parseNumberStrict(timezoneString.substring(3, 5));

                if (hourPart === null || minutePart === null) {
                    return null;
                }

                return hourPart * 60 + minutePart;

            } else if (localTime.includes('-')) {

                const timezoneString: string = localTime.split('-')[1] ?? '';

                if (timezoneString.length !== 5 || timezoneString[2] !== ':') {
                    return null;
                }

                const hourPart: number | null = parseNumberStrict(timezoneString.substring(0, 2));
                const minutePart: number | null = parseNumberStrict(timezoneString.substring(3, 5));

                if (hourPart === null || minutePart === null) {
                    return null;
                }

                return -(hourPart * 60 + minutePart);

            } else {
                return 0;
            }
            
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'ServerTimezoneOffset', e?.message ?? ''));
            return null;  
        }
    }
    
    public async getStatusDates(token: string | null): Promise<StatusDate[]> {

        if (token === null) {
            return [];
        }

        const endpointUrl: string = '/int-v1/purchase/statusDates';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Status Dates', response.status.toString() + ' ' + (await response.text())));
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            if (!Array.isArray(jsonResponse)) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const statusDates: (StatusDate | null)[] = jsonResponse.map((jsonObject: any) => {           
                return StatusDate.fromJson(jsonObject);
            });

            return removeNulls(statusDates);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Status Dates', e?.message ?? ''));
            return [];  
        }
    }

    public async getOrders(token: string | null): Promise<Order[]> {

        if (token === null) {
            return [];
        }

        //no need to filter on statuscode, as it is not really a field in use, and all orders should have statuscode '-'
        const endpointUrl: string = '/int-v1/purchase/orders?$top=1000&$filter=approvedDate is null&$orderBy=changedDate desc'; //this sorting should ensure all relevant orders are included

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Orders', response.status.toString() + ' ' + (await response.text())));
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            if (!Array.isArray(jsonResponse.items)) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
            const orders: (Order | null)[] = jsonResponse.items?.map((jsonObject: any) => {           
                return Order.fromJson(jsonObject);
            });

            return removeNulls(orders);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Orders', e?.message ?? ''));
            return [];  
        }
    }

    public async fetchOrderHTML(orderId: string, token: string | null): Promise<string | null> {

        if (token === null) {
            return null;
        }

        const endpointUrl: string = '/int-v1/purchase/orders/' + orderId + '/html?format=mobile';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Order HTML', response.status.toString() + ' ' + (await response.text())));
                return null;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: string = await response.text();

            return jsonResponse;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Order HTML', e?.message ?? ''));
            return null;  
        }
    }

    public async validateOrder(orderId: string, token: string | null): Promise<OrderValidationResult | null> {

        if (token === null) {
            return null;
        }

        const endpointUrl: string = '/int-v1/purchase/orders/' + orderId + '/validate?operation=approve';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Validate Order', response.status.toString() + ' ' + (await response.text())));
                return null;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            const validationResult: OrderValidationResult = OrderValidationResult.fromJson(jsonResponse);

            return validationResult;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Validate Order', e?.message ?? ''));
            return null;  
        }
    }

    public async setStatusDate(orderId: string, action: 'reqapprove' | 'approve' | 'postpone' | 'requestvesseltoreapprove', postponedToDate: Date | null, token: string | null): Promise<boolean> {

        if (token === null) {
            return false;
        }

        const endpointUrl: string = '/int-v1/purchase/orders/' + orderId + '/statusDate?action=' + action;

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'PUT',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                    body: JSON.stringify({
                        postponedToDate: (action === 'postpone' && postponedToDate !== null) ? datetimeToApiString(postponedToDate, 0) : undefined,
                    }),
                },
            );

            if (response.status >= 400) {
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Set StatusDate', response.status.toString() + ' ' + (await response.text())));
                return false;
            } else {
                return true;
            }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Set StatusDate', e?.message ?? ''));
            return false;  
        }
    }

    public async saveRemark(orderId: string, timestamp: Date, signature: string | null, userId: string | null, remark: string, internal: boolean, includeToSupplier: boolean, serverTimezoneOffsetMinutes: number, token: string | null): Promise<boolean> {

        if (token === null || signature === null || userId === null) {
            return false;
        }

        const endpointUrl: string = '/int-v1/purchase/orders/' + orderId + '/remarks';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'POST',
                    headers: {
                        Authorization: 'Bearer ' + token,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        timeStamp: datetimeToApiString(timestamp, serverTimezoneOffsetMinutes),
                        signature: signature,
                        userId: userId,
                        remark: remark,
                        internal: internal,
                        includeToSupplier: includeToSupplier,
                        sender: 'Purchase',
                    }),
                },
            ); 
            
            if (response.status >= 400) {
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Save Remark', response.status.toString() + ' ' + (await response.text())));
                return false;
            }

            return true;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Save Remark', e?.message ?? ''));
            return false;  
        }
    }

    public async getVessels(token: string | null): Promise<Vessel[]> {

        if (token === null) {
            return [];
        }

        const endpointUrl: string = '/int-v1/vessels';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Vessels', response.status.toString() + ' ' + (await response.text())));
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            if (!Array.isArray(jsonResponse.items)) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
            const vessels: (Vessel | null)[] = jsonResponse.items.map((jsonObject: any) => {           
                return Vessel.fromJson(jsonObject);
            });

            return removeNulls(vessels);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Vessels', e?.message ?? ''));
            return [];  
        }
    }

    public async sendBugReport(bugReport: string, description: string, token: string | null): Promise<boolean> {

        if (token === null) {
            return false;
        }

        const endpointUrl: string = '/int-v1/system/errorlog/mobile';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'POST',
                    headers: {
                        Authorization: 'Bearer ' + token,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        app: 'purchase',
                        log: bugReport,
                        description: description,
                    }),
                },
            ); 
            
            if (response.status >= 400) {
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Send Error Log', response.status.toString() + ' ' + (await response.text())));
                return false;
            }

            return true;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Send Error Log', e?.message ?? ''));
            return false;  
        }
    }
}

export default ApiService;
